import type {
    IFormFieldValue,
    IResponseTiming,
    IResponseTimingInput,
} from "@agrigate/survey-api-def/lib/SurveysService/dto";
import type { MaybeN } from "@agrigate/ts-utils/lib/types";
import { Box } from "@chakra-ui/react";
import {
    type ReactElement,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";
import type { SurveyQuestion } from "../SurveyBuilderForm";
import SurveyQuestionGenerator from "../SurveyQuestionGenerator";
import useQuestionTiming from "./useQuestionTiming";

export interface MultiStepFormHook {
    currentStepIndex: number;
    step: ReactElement | undefined;
    steps: ReactElement[];
    isFirstStep: boolean;
    isLastStep: boolean;
    goTo: (index: number) => void;
    next: () => void;
    back: () => void;
    answers: Record<string, string | string[]>;
    currentBaseQuestionIndex: number;
    totalBaseQuestions: number;
    isCurrentQuestionRequired: boolean;
    isCurrentQuestionAnswered: boolean;
    readTiming: () => IResponseTimingInput;
}

const useMultiStepForm = (
    surveyQuestions: SurveyQuestion[],
    initialResponses?: MaybeN<IFormFieldValue[]>,
    initialTiming?: MaybeN<IResponseTiming>,
): MultiStepFormHook => {
    const [currentStepIndex, setCurrentStepIndex] = useState(0);
    const [answers, setAnswers] = useState<Record<string, string | string[]>>(
        {},
    );

    const currentQuestionId = surveyQuestions[currentStepIndex]?.id;
    const { readTiming } = useQuestionTiming(currentQuestionId, initialTiming);

    const initialAnswers = useMemo(() => {
        const answers: Record<string, string | string[]> = {};
        if (initialResponses) {
            initialResponses.forEach(response => {
                if (response.id && response.value) {
                    answers[response.id] = response.value;
                }
                if (response.levelOneResponses) {
                    response.levelOneResponses.forEach(levelOne => {
                        if (levelOne.id && levelOne.value) {
                            answers[levelOne.id] = levelOne.value;
                        }
                        if (levelOne.levelTwoResponses) {
                            levelOne.levelTwoResponses.forEach(levelTwo => {
                                if (levelTwo.id && levelTwo.value) {
                                    answers[levelTwo.id] = levelTwo.value;
                                }
                            });
                        }
                    });
                }
            });
        }
        return answers;
    }, [initialResponses]);

    useEffect(() => {
        setAnswers(initialAnswers);
    }, [initialAnswers]);

    const buildQuestionWithFollowUps = useCallback(
        (
            question: SurveyQuestion,
            answers: Record<string, string | string[]>,
        ) => {
            const baseQuestion = (
                <SurveyQuestionGenerator
                    key={`base-${question.id}`}
                    surveyQuestion={question}
                    onChange={value => {
                        setAnswers(prev => ({
                            ...prev,
                            [question.id]: value,
                        }));
                    }}
                    value={answers[question.id]}
                />
            );

            const levelOneQuestions: ReactElement[] = [];
            const levelTwoQuestions: ReactElement[] = [];

            if (question.levelOneFollowUpQuestions) {
                question.levelOneFollowUpQuestions.forEach(levelOne => {
                    const baseAnswer = answers[question.id];
                    const condition = levelOne.condition;
                    let isConditionMet = false;

                    if (condition && baseAnswer != null) {
                        isConditionMet = Array.isArray(baseAnswer)
                            ? baseAnswer.includes(condition.id)
                            : baseAnswer === condition.id;
                    }

                    if (isConditionMet) {
                        const levelOneQuestion = (
                            <Box key={`levelOne-${levelOne.id}`} mt={4} pl={4}>
                                <SurveyQuestionGenerator
                                    surveyQuestion={levelOne}
                                    onChange={value => {
                                        setAnswers(prev => ({
                                            ...prev,
                                            [levelOne.id]: value,
                                        }));
                                    }}
                                    value={answers[levelOne.id]}
                                />
                            </Box>
                        );
                        levelOneQuestions.push(levelOneQuestion);

                        if (levelOne.levelTwoFollowUpQuestions) {
                            levelOne.levelTwoFollowUpQuestions.forEach(
                                levelTwo => {
                                    const levelOneAnswer = answers[levelOne.id];
                                    const levelTwoCondition =
                                        levelTwo.condition;
                                    let isLevelTwoConditionMet = false;

                                    if (
                                        levelTwoCondition &&
                                        levelOneAnswer != null
                                    ) {
                                        isLevelTwoConditionMet = Array.isArray(
                                            levelOneAnswer,
                                        )
                                            ? levelOneAnswer.includes(
                                                  levelTwoCondition.id,
                                              )
                                            : levelOneAnswer ===
                                              levelTwoCondition.id;
                                    }

                                    if (isLevelTwoConditionMet) {
                                        const levelTwoQuestion = (
                                            <Box
                                                key={`levelTwo-${levelTwo.id}`}
                                                mt={4}
                                                pl={4}
                                            >
                                                <SurveyQuestionGenerator
                                                    surveyQuestion={levelTwo}
                                                    onChange={value => {
                                                        setAnswers(prev => ({
                                                            ...prev,
                                                            [levelTwo.id]:
                                                                value,
                                                        }));
                                                    }}
                                                    value={answers[levelTwo.id]}
                                                />
                                            </Box>
                                        );
                                        levelTwoQuestions.push(
                                            levelTwoQuestion,
                                        );
                                    }
                                },
                            );
                        }
                    }
                });
            }

            return (
                <Box key={`question-group-${question.id}`}>
                    {baseQuestion}
                    {levelOneQuestions}
                    {levelTwoQuestions}
                </Box>
            );
        },
        [],
    );

    const steps = useMemo(() => {
        return surveyQuestions.map(question =>
            buildQuestionWithFollowUps(question, answers),
        );
    }, [surveyQuestions, answers, buildQuestionWithFollowUps]);

    const next = () => {
        setCurrentStepIndex(i => (i >= steps.length - 1 ? i : i + 1));
    };

    const back = () => {
        setCurrentStepIndex(i => (i <= 0 ? i : i - 1));
    };

    const goTo = (index: number) => {
        if (index >= 0 && index < steps.length) {
            setCurrentStepIndex(index);
        }
    };

    const isCurrentQuestionRequired = useMemo(() => {
        if (steps.length === 0 || currentStepIndex >= steps.length)
            return false;

        const question = surveyQuestions[currentStepIndex];
        return question.isRequired || false;
    }, [currentStepIndex, steps, surveyQuestions]);

    const isCurrentQuestionAnswered = useMemo(() => {
        if (steps.length === 0 || currentStepIndex >= steps.length) return true;

        const question = surveyQuestions[currentStepIndex];
        const baseQuestionId = question.id;

        const baseAnswered =
            answers[baseQuestionId] !== undefined &&
            answers[baseQuestionId] !== "" &&
            (!Array.isArray(answers[baseQuestionId]) ||
                (Array.isArray(answers[baseQuestionId]) &&
                    answers[baseQuestionId].length > 0));

        if (!baseAnswered) return false;

        if (question.levelOneFollowUpQuestions) {
            for (const levelOne of question.levelOneFollowUpQuestions) {
                const baseAnswer = answers[baseQuestionId];
                const condition = levelOne.condition;
                let isConditionMet = false;

                if (condition && baseAnswer != null) {
                    isConditionMet = Array.isArray(baseAnswer)
                        ? baseAnswer.includes(condition.id)
                        : baseAnswer === condition.id;
                }

                if (isConditionMet) {
                    const levelOneAnswered =
                        answers[levelOne.id] !== undefined &&
                        answers[levelOne.id] !== "" &&
                        (!Array.isArray(answers[levelOne.id]) ||
                            (Array.isArray(answers[levelOne.id]) &&
                                answers[levelOne.id].length > 0));

                    if (!levelOneAnswered) return false;

                    if (levelOne.levelTwoFollowUpQuestions) {
                        for (const levelTwo of levelOne.levelTwoFollowUpQuestions) {
                            const levelOneAnswer = answers[levelOne.id];
                            const levelTwoCondition = levelTwo.condition;
                            let isLevelTwoConditionMet = false;

                            if (levelTwoCondition && levelOneAnswer != null) {
                                isLevelTwoConditionMet = Array.isArray(
                                    levelOneAnswer,
                                )
                                    ? levelOneAnswer.includes(
                                          levelTwoCondition.id,
                                      )
                                    : levelOneAnswer === levelTwoCondition.id;
                            }

                            if (isLevelTwoConditionMet) {
                                const levelTwoAnswered =
                                    answers[levelTwo.id] !== undefined &&
                                    answers[levelTwo.id] !== "" &&
                                    (!Array.isArray(answers[levelTwo.id]) ||
                                        (Array.isArray(answers[levelTwo.id]) &&
                                            answers[levelTwo.id].length > 0));

                                if (!levelTwoAnswered) return false;
                            }
                        }
                    }
                }
            }
        }

        return true;
    }, [currentStepIndex, steps, answers, surveyQuestions]);

    const currentBaseQuestionIndex = currentStepIndex;
    const totalBaseQuestions = surveyQuestions.length;
    const isLastStep = currentStepIndex === steps.length - 1;

    return {
        currentStepIndex,
        step: steps[currentStepIndex],
        steps,
        isFirstStep: currentStepIndex === 0,
        isLastStep,
        goTo,
        next,
        back,
        answers,
        currentBaseQuestionIndex,
        totalBaseQuestions,
        isCurrentQuestionRequired,
        isCurrentQuestionAnswered,
        readTiming,
    };
};

export default useMultiStepForm;
